import { Bookmark, BookmarkCheck, Eye, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';

function ArticleCard({ article, token, savedArticleIds, onToggleSave, openAuthModal, onPromote, user }) {
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [imgError, setImgError] = useState(false);
  const isSaved = savedArticleIds?.includes(article.id);
  
  // Check if user is admin
  const ADMIN_EMAILS = ["jschoes@msn.com", "jamespschuler@gmail.com"];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);
  const isPromoted = article.promoted !== null && article.promoted !== undefined;

  const handleArticleClick = async (e) => {
    // Increment view count when article is clicked
    try {
      await axios.get(`${API}/articles/${article.id}`);
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
    // Let the default link behavior continue (open in new tab)
  };

  const handleToggleSave = async (e) => {
    e.preventDefault();
    
    if (!token) {
      openAuthModal('login');
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        await axios.delete(`${API}/articles/${article.id}/save`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/articles/${article.id}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      onToggleSave(article.id, !isSaved);
    } catch (error) {
      console.error('Error toggling save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async (e) => {
    e.preventDefault();
    
    if (!token) {
      openAuthModal('login');
      return;
    }

    setPromoting(true);
    try {
      await axios.post(`${API}/articles/${article.id}/promote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Call parent callback to update article state
      if (onPromote) {
        onPromote(article.id);
      }
    } catch (error) {
      console.error('Error promoting article:', error);
    } finally {
      setPromoting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  // Fallback placeholder image
  const placeholderImage = `https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&h=600&fit=crop`;

  return (
    <a 
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleArticleClick}
      className="block group"
      data-testid={`article-card-${article.id}`}
    >
      <div className="article-card bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:bg-accent/50">
        <div className="flex items-center sm:items-stretch gap-3 p-2.5 sm:gap-4 sm:p-3 md:p-4">
          {/* Image on the left - wider rectangular with more height */}
          <div className="relative flex-shrink-0 w-32 h-28 sm:w-40 sm:h-32 md:w-44 md:h-36 lg:w-52 lg:h-40 rounded-lg overflow-hidden bg-muted">
            <img 
              src={imgError || !article.image_url ? placeholderImage : article.image_url}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={() => setImgError(true)}
              loading="lazy"
            />
            {/* Promoted Badge - visible to everyone */}
            {isPromoted && (
              <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-0.5 sm:gap-1">
                <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                <span className="hidden xs:inline">Promoted</span>
              </div>
            )}
          </div>
          
          {/* Content on the right */}
          <div className="flex-1 flex flex-col justify-between min-w-0 text-left">
            <div className="overflow-hidden">
              <h3 className="font-bold text-base sm:text-lg md:text-lg lg:text-xl mb-2 sm:mb-1 line-clamp-2 group-hover:text-primary transition-colors text-left">
                {article.title}
              </h3>
              <p className="hidden sm:block text-muted-foreground text-sm md:text-base mb-2 line-clamp-2 text-left">
                {article.description}
              </p>
            </div>
            
            <div className="flex items-center justify-between text-[10px] sm:text-xs text-muted-foreground mt-1">
              <div className="flex items-center gap-1 sm:gap-2 text-left min-w-0 flex-1">
                <span className="truncate">{article.publisher_name || 'GameFeed'}</span>
                <span className="flex-shrink-0">·</span>
                <span className="flex-shrink-0">{formatDate(article.published_at)}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                {/* Admin Promote Button - only visible to admins */}
                {isAdmin && (
                  <button
                    onClick={handlePromote}
                    disabled={promoting}
                    className={`p-1 sm:p-1.5 rounded-full transition-colors ${
                      isPromoted 
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                        : 'hover:bg-background'
                    }`}
                    title={isPromoted ? 'Article is promoted' : 'Promote article'}
                    data-testid={`promote-button-${article.id}`}
                  >
                    <TrendingUp className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isPromoted ? 'text-white' : ''}`} />
                  </button>
                )}
                
                {/* Save Button */}
                <button
                  onClick={handleToggleSave}
                  disabled={saving}
                  className="p-1 sm:p-1.5 hover:bg-background rounded-full transition-colors"
                  data-testid={`save-button-${article.id}`}
                >
                  {isSaved ? (
                    <BookmarkCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                  ) : (
                    <Bookmark className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}

export default ArticleCard;